import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";
import { subscribeHabitChanges, publishHabitRefresh } from "@/lib/habitChanges";

export const HABITS_CHANGED = "vyv:habits-changed";
export const notifyHabitsChanged = (userId?: string) => userId ? publishHabitRefresh(userId) : window.dispatchEvent(new CustomEvent(HABITS_CHANGED));
export type HabitsStatus = "loading" | "ready" | "error" | "signed-out";

/** Account-bound reads shared by the active Habits routes. */
export function useHabitsResource<T>(loader: (userId: string, assertCurrent: () => void, accessToken: string) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [status, setStatus] = useState<HabitsStatus>("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [identityGeneration, setIdentityGeneration] = useState(0);
  const owner = useRef<string | null | undefined>(undefined);
  const generation = useRef(0);
  const request = useRef(0);
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    const ticket = ++request.current;
    let epoch = generation.current, uid = owner.current;
    const current = () => mounted.current && ticket === request.current && epoch === generation.current && owner.current === uid;
    const assertCurrent = () => { if (!current()) throw new Error("Habits session changed"); };
    setIsRefreshing(true);
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      assertCurrent();
      if (error) throw error;
      const next = user?.id ?? null;
      if (owner.current !== next) {
        epoch = ++generation.current; owner.current = next; uid = next; setIdentityGeneration(epoch);
        setUserId(next); setData(null); setCheckedAt(null);
      }
      if (!next) { setStatus("signed-out"); return; }
      const session = await supabase.auth.getSession(); assertCurrent();
      if (session.error || session.data.session?.user.id !== next || !session.data.session.access_token) throw new Error("Habits session changed");
      const result = await loader(next, assertCurrent, session.data.session.access_token);
      assertCurrent(); setData(result); setStatus("ready"); setCheckedAt(Date.now()); return result;
    } catch (error) {
      if (current()) { console.error("[Habits] owned read unavailable", error instanceof Error ? error.message : "read_unconfirmed"); setStatus("error"); }
    } finally {
      if (current()) setIsRefreshing(false);
    }
    return null;
  }, [loader]);

  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (!mounted.current || owner.current === next) return;
      owner.current = next; generation.current++; request.current++;
      const epoch = generation.current; setIdentityGeneration(epoch);
      setUserId(next); setData(null); setCheckedAt(null); setIsRefreshing(false);
      setStatus(next ? "loading" : "signed-out");
      if (next) queueMicrotask(() => { if (mounted.current && owner.current === next && generation.current === epoch) void refresh(); });
    });
    void refresh();
    const changed = (event: Event) => {
      const target = (event as CustomEvent<{ userId?: string }>).detail?.userId;
      if (mounted.current && owner.current && (!target || target === owner.current)) void refresh();
    };
    const unsubscribeChanges = subscribeHabitChanges(uid => { if (mounted.current && uid === owner.current) void refresh(); });
    window.addEventListener(HABITS_CHANGED, changed);
    const lifetime = generation, reads = request;
    return () => { mounted.current = false; lifetime.current++; reads.current++; subscription.unsubscribe(); unsubscribeChanges(); window.removeEventListener(HABITS_CHANGED, changed); };
  }, [refresh]);

  useAgendaAutoRefresh({ checkedAt, nextEvent: null, loading: isRefreshing, error: status === "error", enabled: !!userId || status === "error", refresh });
  const commit = useCallback((update: (previous: T | null) => T) => {
    request.current++; setData(update); setCheckedAt(Date.now()); setIsRefreshing(false);
  }, []);
  const capture = useCallback(() => {
    const epoch = generation.current, uid = owner.current;
    return () => mounted.current && owner.current === uid && !!uid && generation.current === epoch;
  }, []);
  return { data, userId, status, isRefreshing, checkedAt, refresh, capture, commit, identityGeneration };
}
